import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Save, Trash2, Edit2, ArrowLeft, X, Upload, Link as LinkIcon, Calendar, Clock, Search, Filter, FileText, MapPin } from 'lucide-react';
import { listEventsWithMeta, createEvent, updateEvent, deleteEvent, adminVerify, DEFAULT_BASE_URL } from '../../services/api';
import WysiwygEditor from '../../components/WysiwygEditor';
import toast from 'react-hot-toast';

const emptyForm = { 
  title: '', 
  description: '', 
  date: '', 
  startDate: '',
  endDate: '',
  time: '', 
  startTime: '',
  endTime: '',
  location: '', 
  category: 'General',
  imageUrl: '',
  registrationRequired: false,
  eventRegistrationStartDateTime: '',
  eventRegistrationEndDateTime: '',
  maxParticipants: '',
  fees: { amount: 0, currency: 'INR' },
  status: 'Upcoming',
  isPublished: true
};

const getApiOrigin = () => {
  try {
    const apiBase = DEFAULT_BASE_URL.replace(/\/$/, '');
    const parsed = new URL(apiBase);
    return `${parsed.protocol}//${parsed.host}`;
  } catch {
    return window.location.origin;
  }
};

const getImageSrc = (url) => {
  if (!url) return '';
  if (url.startsWith('http') || url.startsWith('data:') || url.startsWith('blob:')) return url;
  if (url.startsWith('/')) return `${getApiOrigin()}${url}`;
  return url;
};

const MAX_IMAGE_SIZE_BYTES = 1 * 1024 * 1024;

const pad2 = (value) => String(value).padStart(2, '0');

const toDateTimeLocalInputValue = (dateValue) => {
  if (!dateValue) return '';
  const d = new Date(dateValue);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

const toIsoFromDateTimeLocal = (dateTimeLocalString) => {
  if (!dateTimeLocalString) return undefined;
  const d = new Date(dateTimeLocalString);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
};

const AdminEventsPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [imageInputMode, setImageInputMode] = useState('url');
  const [imageFile, setImageFile] = useState(null);
  const [existingImageUrl, setExistingImageUrl] = useState('');
  const [imagePreviewUrl, setImagePreviewUrl] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [filters, setFilters] = useState({
    title: '',
    category: '',
    status: '',
    location: '',
    startDate: '',
    endDate: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  });
  const navigate = useNavigate();

  const categories = ['General', 'Workshop', 'Seminar', 'Training', 'Meeting', 'Conference', 'Webinar', 'Exhibition'];

  const refresh = useCallback(async (options = {}) => {
    setLoading(true);
    setError('');
    const clearFilters = options.clearFilters === true;
    try {
      const params = clearFilters
        ? { page: 1, limit: pagination.limit }
        : {
            ...filters,
            page: pagination.page,
            limit: pagination.limit
          };

      if (!clearFilters) {
        Object.keys(params).forEach(key => {
          if (!params[key]) delete params[key];
        });
      }

      const data = await listEventsWithMeta(params);
      const events = (data.events || []).sort((a, b) => {
        const dateA = new Date(a.startDate || a.date || a.createdAt || 0);
        const dateB = new Date(b.startDate || b.date || b.createdAt || 0);
        return dateB - dateA;
      });

      setItems(events);
      setPagination(prev => ({
        ...prev,
        page: clearFilters ? 1 : prev.page,
        total: data.total || 0,
        totalPages: data.totalPages || 0
      }));
      if (clearFilters) {
        setFilters({
          title: '',
          category: '',
          status: '',
          location: '',
          startDate: '',
          endDate: ''
        });
      }
      return events;
    } catch (e) {
      const errorMsg = e.message || 'Failed to load events';
      setError(errorMsg);
      toast.error(errorMsg);
      return [];
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.limit]);

  useEffect(() => {
    const init = async () => {
      try {
        await adminVerify();
        setIsVerified(true);
      } catch (e) {
        navigate('/admin/login');
      }
    };
    init();
  }, [navigate]);

  useEffect(() => {
    if (!imageFile) {
      setImagePreviewUrl('');
      return undefined;
    }

    const preview = URL.createObjectURL(imageFile);
    setImagePreviewUrl(preview);

    return () => URL.revokeObjectURL(preview);
  }, [imageFile]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  };

  const handleImageFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      setImageFile(null);
      return;
    }
    if (!file.type.startsWith('image/')) {
      toast.error('Please select a valid image file');
      event.target.value = '';
      return;
    }
    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      toast.error('Image size must be 1MB or less');
      event.target.value = '';
      return;
    }
    setImageFile(file);
    setForm(prev => ({ ...prev, imageUrl: '' }));
  };

  useEffect(() => {
    if (!isVerified) return;
    refresh();
  }, [isVerified, refresh]);

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      if (!form.title || !form.description || !form.startDate || !form.location || !form.category) {
        throw new Error('Please fill all required fields');
      }

      const payload = {
        title: form.title,
        description: form.description,
        date: form.startDate || form.date,
        startDate: form.startDate || form.date,
        endDate: form.endDate || form.startDate || form.date,
        time: form.startTime || form.time,
        startTime: form.startTime || form.time,
        endTime: form.endTime || form.startTime || form.time,
        location: form.location,
        category: form.category,
        imageUrl: form.imageUrl,
        registrationRequired: form.registrationRequired,
        eventRegistrationStartDateTime: form.registrationRequired
          ? (form.eventRegistrationStartDateTime ? toIsoFromDateTimeLocal(form.eventRegistrationStartDateTime) : null)
          : null,
        eventRegistrationEndDateTime: form.registrationRequired
          ? (form.eventRegistrationEndDateTime ? toIsoFromDateTimeLocal(form.eventRegistrationEndDateTime) : null)
          : null,
        maxParticipants: form.maxParticipants ? parseInt(form.maxParticipants) : undefined,
        fees: {
          amount: form.fees?.amount ? Math.round((parseFloat(form.fees.amount) || 0) * 100) / 100 : 0,
          currency: form.fees?.currency || 'INR'
        },
        status: form.status,
        isPublished: form.isPublished
      };

      if (payload.eventRegistrationStartDateTime && payload.eventRegistrationEndDateTime) {
        const start = new Date(payload.eventRegistrationStartDateTime);
        const end = new Date(payload.eventRegistrationEndDateTime);
        if (start > end) {
          throw new Error('Registration start must be before registration end');
        }
      }

      const shouldUseUpload = imageInputMode === 'upload';
      let requestBody = payload;

      if (shouldUseUpload && imageFile) {
        const formData = new FormData();
        // Redundant full payload for compatibility with strict multipart parsers/proxies.
        formData.append('__payload', JSON.stringify(payload));
        Object.entries(payload).forEach(([key, value]) => {
          if (value === undefined || value === null) return;
          if (typeof value === 'object' && !(value instanceof Date)) {
            formData.append(key, JSON.stringify(value));
            return;
          }
          formData.append(key, value);
        });
        formData.append('image', imageFile);
        requestBody = formData;
      } else if (imageInputMode === 'url') {
        payload.imageUrl = form.imageUrl;
      } else if (editingId && existingImageUrl) {
        payload.imageUrl = existingImageUrl;
      }

      let savedEvent;
      if (editingId) {
        savedEvent = await updateEvent(editingId, requestBody);
        setSuccess('Event updated successfully!');
      } else {
        savedEvent = await createEvent(requestBody);
        setSuccess('Event created successfully!');
      }

      setForm(emptyForm);
      setImageFile(null);
      setExistingImageUrl('');
      setImageInputMode('url');
      setEditingId(null);
      setShowForm(false);
      // Clear filters and refetch so status reflects latest date/time rules
      await refresh({ clearFilters: true });
      toast.success(editingId ? 'Event updated successfully!' : 'Event created successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      const errorMsg = e.message || 'Save failed';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    try {
      await deleteEvent(id);
      setSuccess('Event deleted successfully!');
      toast.success('Event deleted successfully!');
      await refresh();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      const errorMsg = e.message || 'Delete failed';
      setError(errorMsg);
      toast.error(errorMsg);
    }
  }

  function startEdit(item) {
    const itemImage = item.imageUrl || item.image || '';
    setEditingId(item._id);
    setForm({
      title: item.title || '',
      description: item.description || '',
      date: item.startDate ? String(item.startDate).slice(0, 10) : (item.date ? String(item.date).slice(0, 10) : ''),
      startDate: item.startDate ? String(item.startDate).slice(0, 10) : (item.date ? String(item.date).slice(0, 10) : ''),
      endDate: item.endDate ? String(item.endDate).slice(0, 10) : '',
      time: item.startTime || item.time || '',
      startTime: item.startTime || item.time || '',
      endTime: item.endTime || '',
      location: item.location || '',
      category: item.category || 'General',
      imageUrl: itemImage,
      registrationRequired: item.registrationRequired || false,
      eventRegistrationStartDateTime: toDateTimeLocalInputValue(item.eventRegistrationStartDateTime),
      eventRegistrationEndDateTime: toDateTimeLocalInputValue(item.eventRegistrationEndDateTime),
      maxParticipants: item.maxParticipants || '',
      fees: item.fees || { amount: 0, currency: 'INR' },
      status: item.status || 'Upcoming',
      isPublished: item.isPublished !== undefined ? item.isPublished : true
    });
    setImageFile(null);
    setExistingImageUrl(itemImage);
    setImageInputMode(itemImage ? 'url' : 'upload');
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function handleNew() {
    setEditingId(null);
    setForm(emptyForm);
    setImageInputMode('url');
    setImageFile(null);
    setExistingImageUrl('');
    setShowForm(true);
    setError('');
    setSuccess('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function handleCancel() {
    setEditingId(null);
    setForm(emptyForm);
    setImageInputMode('url');
    setImageFile(null);
    setExistingImageUrl('');
    setShowForm(false);
    setError('');
    setSuccess('');
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatDateTime = (dateValue) => {
    if (!dateValue) return '';
    const d = new Date(dateValue);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-emerald-50">
      <header className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/admin')} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
              <ArrowLeft className="h-5 w-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">{'Events'}</h1>
              <p className="text-xs text-gray-500">Create and manage events</p>
            </div>
          </div>
          {!showForm && (
            <button
              onClick={handleNew}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-primary-600 to-primary-700 text-white px-6 py-2.5 rounded-lg hover:from-primary-700 hover:to-primary-800 transition-all shadow-md hover:shadow-lg font-semibold text-sm"
            >
              <Plus className="h-5 w-5" />
              {'Add New Event'}
            </button>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {error && (
          <div className="mb-6 p-4 rounded-xl bg-red-50 border-l-4 border-red-500 text-red-700 flex items-center justify-between shadow-sm">
            <div className="flex items-center gap-2">
              <span className="font-medium">{error}</span>
            </div>
            <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 transition-colors">
              <X className="h-5 w-5" />
            </button>
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 rounded-xl bg-green-50 border-l-4 border-green-500 text-green-700 flex items-center justify-between shadow-sm">
            <div className="flex items-center gap-2">
              <span className="font-medium">{success}</span>
            </div>
            <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700 transition-colors">
              <X className="h-5 w-5" />
            </button>
          </div>
        )}

        {/* Form */}
        {showForm && (
          <div className="bg-white shadow-xl rounded-2xl p-8 mb-8 border border-gray-200">
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {editingId ? 'Edit Event' : 'Add New Event'}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {editingId ? 'Update the event details below' : 'Fill in the form below to create a new event'}
                </p>
              </div>
              <button
                onClick={handleCancel}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X className="h-5 w-5 text-gray-600" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {'Event Title'} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    value={form.title}
                    onChange={e => setForm({ ...form, title: e.target.value })}
                    required
                    placeholder="Enter event title"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {'Category'} <span className="text-red-500">*</span>
                  </label>
                  <select
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    value={form.category}
                    onChange={e => setForm({ ...form, category: e.target.value })}
                    required
                  >
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    value={form.status}
                    onChange={e => setForm({ ...form, status: e.target.value })}
                  >
                    <option value="Upcoming">Upcoming</option>
                    <option value="Ongoing">Ongoing</option>
                    <option value="Completed">Completed</option>
                    <option value="Cancelled">Cancelled</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {'Start Date'} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    value={form.startDate}
                    onChange={e => setForm({ ...form, startDate: e.target.value, date: e.target.value })}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {'End Date'}
                  </label>
                  <input
                    type="date"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    value={form.endDate}
                    onChange={e => setForm({ ...form, endDate: e.target.value })}
                    min={form.startDate}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {'Start Time'} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="time"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    value={form.startTime}
                    onChange={e => setForm({ ...form, startTime: e.target.value, time: e.target.value })}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {'End Time'}
                  </label>
                  <input
                    type="time"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    value={form.endTime}
                    onChange={e => setForm({ ...form, endTime: e.target.value })}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {'Location'} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    value={form.location}
                    onChange={e => setForm({ ...form, location: e.target.value })}
                    required
                    placeholder="Enter event location"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Registration Fee (₹)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    value={form.fees?.amount || 0}
                    onChange={e => setForm({ 
                      ...form, 
                      fees: { ...form.fees, amount: Math.round((parseFloat(e.target.value) || 0) * 100) / 100 } 
                    })}
                    placeholder="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Max Participants
                  </label>
                  <input
                    type="number"
                    min="0"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    value={form.maxParticipants}
                    onChange={e => setForm({ ...form, maxParticipants: e.target.value })}
                    placeholder="Leave empty for unlimited"
                  />
                </div>

                {form.registrationRequired && (
                  <>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Registration Start (Date &amp; Time)
                      </label>
                      <input
                        type="datetime-local"
                        className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        value={form.eventRegistrationStartDateTime}
                        onChange={e => setForm({ ...form, eventRegistrationStartDateTime: e.target.value })}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Registration End (Date &amp; Time)
                      </label>
                      <input
                        type="datetime-local"
                        className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        value={form.eventRegistrationEndDateTime}
                        min={form.eventRegistrationStartDateTime || undefined}
                        onChange={e => setForm({ ...form, eventRegistrationEndDateTime: e.target.value })}
                      />
                    </div>
                  </>
                )}

                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Event Image
                  </label>
                  <div className="space-y-3">
                    <div className="flex flex-wrap gap-4">
                      <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                        <input
                          type="radio"
                          name="event-image-mode"
                          checked={imageInputMode === 'upload'}
                          onChange={() => {
                            setImageInputMode('upload');
                            setForm(prev => ({ ...prev, imageUrl: '' }));
                          }}
                        />
                        <Upload className="h-4 w-4" />
                        Upload Image
                      </label>
                      <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                        <input
                          type="radio"
                          name="event-image-mode"
                          checked={imageInputMode === 'url'}
                          onChange={() => {
                            setImageInputMode('url');
                            setImageFile(null);
                          }}
                        />
                        <LinkIcon className="h-4 w-4" />
                        Use Image URL
                      </label>
                    </div>

                    {imageInputMode === 'upload' ? (
                      <div className="space-y-2">
                        <input
                          type="file"
                          accept="image/*"
                          className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          onChange={handleImageFileChange}
                        />
                        <p className="text-xs text-gray-500">
                          Upload JPG, PNG, WEBP, etc. Max size is 1MB.
                        </p>
                      </div>
                    ) : (
                      <input
                        type="url"
                        className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        value={form.imageUrl}
                        onChange={e => {
                          setForm({ ...form, imageUrl: e.target.value });
                          setImageFile(null);
                        }}
                        placeholder="https://example.com/image.jpg"
                      />
                    )}

                    {(imageFile || form.imageUrl || existingImageUrl) && (
                      <div className="border border-gray-200 rounded-lg p-3">
                        <p className="text-xs font-medium text-gray-600 mb-2">Image Preview</p>
                        <img
                          src={
                            imagePreviewUrl || getImageSrc(form.imageUrl || existingImageUrl)
                          }
                          alt="Event preview"
                          className="h-28 w-auto rounded-md border border-gray-200 object-cover"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {'Event Description'} <span className="text-red-500">*</span>
                  </label>
                  <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-transparent">
                    <WysiwygEditor
                    value={form.description}
                      onChange={(content) => setForm({ ...form, description: content })}
                      placeholder="Enter event description with rich text formatting..."
                      height="300px"
                  />
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Use the toolbar to format text: bold, italic, colors, lists, and more
                  </p>
                </div>

                <div className="md:col-span-2 flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
                      checked={form.registrationRequired}
                      onChange={e => {
                        const checked = e.target.checked;
                        setForm(prev => ({
                          ...prev,
                          registrationRequired: checked,
                          ...(checked
                            ? {}
                            : { eventRegistrationStartDateTime: '', eventRegistrationEndDateTime: '' })
                        }));
                      }}
                    />
                    <span className="text-sm font-medium text-gray-700">Registration Required</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
                      checked={form.isPublished}
                      onChange={e => setForm({ ...form, isPublished: e.target.checked })}
                    />
                    <span className="text-sm font-medium text-gray-700">Published</span>
                  </label>
                </div>
              </div>

              <div className="flex items-center gap-4 pt-6 border-t border-gray-200">
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-primary-600 to-primary-700 text-white px-8 py-3 rounded-lg hover:from-primary-700 hover:to-primary-800 disabled:opacity-60 disabled:cursor-not-allowed transition-all shadow-lg font-semibold"
                >
                  <Save className="h-5 w-5" />
                  {saving ? ('Loading...') : editingId ? 'Update' : 'Create'}
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-6 py-3 rounded-lg border-2 border-gray-300 hover:bg-gray-50 transition-colors font-medium"
                >
                  {'Cancel'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-500" />
              <h3 className="text-sm font-semibold text-gray-900">{'Filters'}</h3>
              {Object.values(filters).some(f => f) && (
                <span className="px-2 py-0.5 bg-primary-100 text-primary-700 text-xs font-semibold rounded-full">
                  {Object.values(filters).filter(f => f).length} active
                </span>
              )}
            </div>
            {Object.values(filters).some(f => f) && (
              <button
                onClick={() => {
                  setFilters({
                    title: '',
                    category: '',
                    status: '',
                    location: '',
                    startDate: '',
                    endDate: ''
                  });
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 font-medium transition-colors"
              >
                <X className="h-3.5 w-3.5" />
                {'Clear All'}
              </button>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
            {/* Title Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                {'Title'}
              </label>
              <div className="relative">
                <FileText className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  className={`w-full border border-gray-300 rounded-lg pl-9 ${filters.title ? 'pr-9' : 'pr-3'} py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all`}
                  placeholder="Search title..."
                  value={filters.title}
                  onChange={(e) => handleFilterChange('title', e.target.value)}
                />
                {filters.title && (
                  <button
                    onClick={() => handleFilterChange('title', '')}
                    className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    type="button"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>

            {/* Category Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                {'Category'}
              </label>
              <select
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
              >
                <option value="">All Categories</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Status
              </label>
              <select
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">All Statuses</option>
                <option value="Upcoming">Upcoming</option>
                <option value="Ongoing">Ongoing</option>
                <option value="Completed">Completed</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>

            {/* Location Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                {'Location'}
              </label>
              <div className="relative">
                <MapPin className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  className={`w-full border border-gray-300 rounded-lg pl-9 ${filters.location ? 'pr-9' : 'pr-3'} py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all`}
                  placeholder="Search location..."
                  value={filters.location}
                  onChange={(e) => handleFilterChange('location', e.target.value)}
                />
                {filters.location && (
                  <button
                    onClick={() => handleFilterChange('location', '')}
                    className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    type="button"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>

            {/* Start Date */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                From Date
              </label>
              <input
                type="date"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
              />
            </div>

            {/* End Date */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                To Date
              </label>
              <input
                type="date"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                min={filters.startDate}
              />
            </div>
          </div>

        </div>

        {/* Events List */}
        <div className="bg-white shadow-xl rounded-2xl border border-gray-200 overflow-hidden">
          <div className="px-8 py-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
              <p className="text-gray-600">{'Loading...'}</p>
            </div>
          ) : items.length === 0 ? (
            // Check if filters are active or if it's truly empty
            (filters.title || filters.category || filters.status || filters.location || filters.startDate || filters.endDate) ? (
              <div className="p-12 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                  <Search className="h-8 w-8 text-gray-400" />
                </div>
                <p className="text-gray-600 mb-2 font-medium">No events found.</p>
                <p className="text-sm text-gray-500 mb-6">Try adjusting your filters</p>
                <button 
                  onClick={() => {
                    setFilters({
                      title: '',
                      category: '',
                      status: '',
                      location: '',
                      startDate: '',
                      endDate: ''
                    });
                    setPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-all shadow-lg font-semibold"
                >
                  Clear Filters
                </button>
              </div>
            ) : (
              <div className="p-12 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary-100 mb-4">
                  <Calendar className="h-8 w-8 text-primary-600" />
              </div>
              <p className="text-gray-600 mb-2 font-medium">No events found. Create your first event!</p>
              <p className="text-sm text-gray-500 mb-6">Get started by creating your first event</p>
              <button 
                onClick={handleNew}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg hover:from-primary-700 hover:to-primary-800 transition-all shadow-lg font-semibold"
              >
                <Plus className="h-5 w-5" /> Create Your First Event
              </button>
            </div>
              )
            ) : (
            <div className="divide-y divide-gray-200">
              {items.map(item => (
                <div key={item._id} className="p-6 hover:bg-gradient-to-r hover:from-gray-50 hover:to-emerald-50 transition-all group">
                  <div className="flex items-start justify-between gap-6">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-3 mb-3 flex-wrap">
                        <h4 className="text-lg font-bold text-gray-900 flex-1 min-w-[200px]">{item.title}</h4>
                        <span className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap border shadow-sm ${
                          item.status === 'Upcoming' ? 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 border-green-200' :
                          item.status === 'Ongoing' ? 'bg-gradient-to-r from-yellow-100 to-amber-100 text-yellow-800 border-yellow-200' :
                          item.status === 'Completed' ? 'bg-gradient-to-r from-gray-100 to-slate-100 text-gray-800 border-gray-200' :
                          'bg-gradient-to-r from-red-100 to-rose-100 text-red-800 border-red-200'
                        }`}>
                          {item.status}
                        </span>
                        <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-800 whitespace-nowrap border border-blue-200 shadow-sm">
                          {item.category}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-4 line-clamp-2 leading-relaxed">{item.description}</p>
                      <div className="flex flex-wrap items-center gap-3 text-xs">
                        <span className="flex items-center gap-1.5 px-2 py-1 bg-gray-50 rounded-md text-gray-600">
                          <Calendar className="h-3.5 w-3.5 text-gray-500" />
                          <span className="font-medium text-gray-700">{formatDate(item.startDate || item.date)}</span>
                        </span>
                        {item.registrationRequired && (item.eventRegistrationStartDateTime || item.eventRegistrationEndDateTime) && (
                          <span className="flex items-center gap-1.5 px-2 py-1 bg-gray-50 rounded-md text-gray-600">
                            <Clock className="h-3.5 w-3.5 text-gray-500" />
                            <span className="text-gray-700">
                              {(() => {
                                const startRaw = item.eventRegistrationStartDateTime || null;
                                const endRaw = item.eventRegistrationEndDateTime || null;
                                const start = startRaw ? new Date(startRaw) : null;
                                const end = endRaw ? new Date(endRaw) : null;
                                if (start && end && !Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime()) && start > end) {
                                  return 'Reg window invalid';
                                }
                                if (startRaw && endRaw) {
                                  const startLabel = formatDateTime(startRaw);
                                  const endLabel = formatDateTime(endRaw);
                                  return startLabel && endLabel ? `Reg: ${startLabel} - ${endLabel}` : 'Reg window set';
                                }
                                if (startRaw) {
                                  const startLabel = formatDateTime(startRaw);
                                  return startLabel ? `Reg opens: ${startLabel}` : 'Reg opens set';
                                }
                                const endLabel = formatDateTime(endRaw);
                                return endLabel ? `Reg closes: ${endLabel}` : 'Reg closes set';
                              })()}
                            </span>
                          </span>
                        )}
                        <span className="flex items-center gap-1.5 px-2 py-1 bg-gray-50 rounded-md text-gray-600">
                          <span className="text-gray-500">🕐</span>
                          <span className="text-gray-700">{item.startTime || item.time || 'TBD'}</span>
                        </span>
                        <span className="flex items-center gap-1.5 px-2 py-1 bg-gray-50 rounded-md text-gray-600">
                          <span className="text-gray-500">📍</span>
                          <span className="text-gray-700">{item.location}</span>
                        </span>
                        {item.maxParticipants && (
                          <span className="flex items-center gap-1.5 px-2 py-1 bg-gray-50 rounded-md text-gray-600">
                            <span className="text-gray-500">👥</span>
                            <span className="text-gray-700">{item.currentParticipants || 0}/{item.maxParticipants}</span>
                          </span>
                        )}
                        <span className="flex items-center gap-1.5 px-2 py-1 bg-gray-50 rounded-md text-gray-600">
                          <span className="text-gray-500">💰</span>
                          <span className="text-gray-700 font-medium">{item.fees?.amount ? `₹${item.fees.amount}` : 'Free'}</span>
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => startEdit(item)}
                        className="p-3 border-2 border-blue-200 rounded-lg hover:bg-blue-50 hover:border-blue-400 transition-all shadow-sm group-hover:shadow-md"
                        title="Edit event"
                      >
                        <Edit2 className="h-5 w-5 text-blue-600" />
                      </button>
                      <button
                        onClick={() => handleDelete(item._id)}
                        className="p-3 border-2 border-red-200 rounded-lg hover:bg-red-50 hover:border-red-400 transition-all shadow-sm group-hover:shadow-md"
                        title="Delete event"
                      >
                        <Trash2 className="h-5 w-5 text-red-600" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="px-8 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                {'Page'} {pagination.page} {'of'} {pagination.totalPages} ({pagination.total || items.length} {'total'})
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                  disabled={pagination.page === 1}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {'Previous'}
                </button>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))}
                  disabled={pagination.page === pagination.totalPages}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {'Next'}
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminEventsPage;
